/**
 * One-time signup bonus (30 AI HQ credits) — client-side trigger.
 *
 * Called from authStore's SIGNED_IN handler, in the same `isNew` branch that
 * already fires the GA `sign_up` event (see authStore.ts) — that branch is
 * the single source of truth for "this is a fresh signup, not a login",
 * derived by comparing auth created_at vs last_sign_in_at, and already
 * covers email signup, magic link, and OAuth (Google/GitHub/Discord) since
 * all of them funnel through the same onAuthStateChange listener.
 *
 * Server-side idempotency (credit_transactions unique guard, see
 * app/api/credits/signup-bonus/route.ts) is the real guarantee against a
 * double grant. This sessionStorage guard just avoids a redundant network
 * call firing more than once per browser tab session.
 */
import type { User } from '@supabase/supabase-js';
import { useCreditStore } from '../stores/creditStore';
import { useSignupBonusToastStore } from '../stores/signupBonusToastStore';
import { trackEvent } from './analytics';
import { detectLang } from '../hooks/useLanguage';

const ATTEMPT_KEY_PREFIX = 'la_signup_bonus_attempted_';

const TOAST_TEXT: Record<'ja' | 'en', string> = {
  ja: '🎁 登録ボーナス: AI HQ 30分ぶんのクレジットを付与しました',
  en: '🎁 Signup bonus: 30 AI HQ credits have been added to your account',
};

interface SignupBonusResponse {
  granted?: boolean;
  balance?: number;
}

export async function maybeGrantSignupBonus(user: User, accessToken: string): Promise<void> {
  if (typeof window === 'undefined' || !accessToken) return;

  const attemptKey = `${ATTEMPT_KEY_PREFIX}${user.id}`;
  try {
    if (sessionStorage.getItem(attemptKey)) return;
    sessionStorage.setItem(attemptKey, '1');
  } catch {
    // No sessionStorage (private mode etc.) — proceed anyway; server-side
    // idempotency is the real guarantee against granting twice.
  }

  try {
    const res = await fetch('/api/credits/signup-bonus', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return;

    const data = (await res.json()) as SignupBonusResponse;
    if (!data.granted) return;

    if (typeof data.balance === 'number') {
      // Account-scoped: a grant for a user who has since signed out must not
      // land in the next user's store.
      useCreditStore.getState().applyGrantedBalance(user.id, data.balance);
    } else {
      void useCreditStore.getState().fetchBalance(user.id).catch(() => undefined);
    }

    trackEvent('signup_bonus_granted');

    const lang = detectLang() === 'ja' ? 'ja' : 'en';
    useSignupBonusToastStore.getState().show(TOAST_TEXT[lang]);
  } catch {
    // Best-effort — a missed bonus toast isn't worth surfacing an error for.
  }
}
